import { useEffect } from "react";
import { LOCAL_STORAGE_BASE_KEYS, Shop } from "@/types/v2";
import { localStorageManager } from "@/services/localStorage";
import {
  SortingState,
  MultiLevelSortConfig,
  DEFAULT_SORTING_STATE,
} from "../types";
import { useMultiLevelSorting } from "./useMultiLevelSorting";

interface UsePersistentSortingProps {
  config?: Partial<MultiLevelSortConfig>;
  storageKey?: string;
  shopId?: string;
  shop?: Shop;
}

export const usePersistentSorting = ({
  config = {},
  storageKey = LOCAL_STORAGE_BASE_KEYS.DASHBOARD_LIST_SORTING,
  shopId,
  shop,
}: UsePersistentSortingProps = {}) => {
  // Load initial sorting state from localStorage
  const loadSortingState = (): SortingState => {
    try {
      const stored = localStorageManager.getItem(storageKey, shopId);
      if (stored) {
        const parsed = JSON.parse(stored) as SortingState;

        // Validate the loaded state
        if (parsed && Array.isArray(parsed.criteria)) {
          return {
            ...DEFAULT_SORTING_STATE,
            ...parsed,
          };
        }
      }
    } catch (error) {
      console.warn("Failed to load sorting preferences:", error);
    }

    return DEFAULT_SORTING_STATE;
  };

  const initialSorting =
    config.persistUserPreferences !== false
      ? loadSortingState()
      : DEFAULT_SORTING_STATE;

  const sorting = useMultiLevelSorting({
    config,
    initialSorting,
    shop,
  });

  // Save sorting state to localStorage whenever it changes
  useEffect(() => {
    if (config.persistUserPreferences !== false) {
      try {
        localStorageManager.setItem(
          storageKey,
          JSON.stringify(sorting.sortingState),
          shopId
        );
      } catch (error) {
        console.warn("Failed to save sorting preferences:", error);
      }
    }
  }, [sorting.sortingState, storageKey, shopId, config.persistUserPreferences]);

  // Function to reset preferences to default
  const resetSortingPreferences = () => {
    try {
      localStorageManager.setItem(storageKey, "", shopId);
      sorting.setSortingState(DEFAULT_SORTING_STATE);
    } catch (error) {
      console.warn("Failed to reset sorting preferences:", error);
    }
  };

  // Function to export sorting preferences
  const exportSortingPreferences = () => {
    return {
      sortingState: sorting.sortingState,
      timestamp: new Date().toISOString(),
      version: "1.0",
    };
  };

  // Function to import sorting preferences
  const importSortingPreferences = (data: any) => {
    try {
      if (
        data &&
        data.sortingState &&
        Array.isArray(data.sortingState.criteria)
      ) {
        const importedState = {
          ...DEFAULT_SORTING_STATE,
          ...data.sortingState,
        };
        sorting.setSortingState(importedState);

        if (config.persistUserPreferences !== false) {
          localStorageManager.setItem(
            storageKey,
            JSON.stringify(importedState),
            shopId
          );
        }

        return true;
      }
    } catch (error) {
      console.warn("Failed to import sorting preferences:", error);
    }
    return false;
  };

  return {
    ...sorting,
    resetSortingPreferences,
    exportSortingPreferences,
    importSortingPreferences,
    storageKey,
  };
};
